import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { Award } from 'lucide-react';
import MDVIPCallToAction from '../components/MDVIPCallToAction';
import AnimatedPage from '../components/AnimatedPage';

const MeetTheDoctor = () => {
  const [doctorRef, doctorVisible] = useScrollAnimation(0.1);
  
  const credentials = [
    { id: 'presidential-1985', text: 'Presidential Achievement Award Scholarship, Texas A&M University (1985)' },
    { id: 'scholarship-1999', text: 'T. Crawford, M.D. Award Scholarship, Howard University (1999)' },
    { id: 'wmar-2007', text: 'WMAR 2 News Segment: "Early Detection of Lung Cancer"  October 18th, 2007' }
  ];

  return (
    <AnimatedPage className="min-h-screen">
      {/* Hero Section with Image and Biography */}
      <section ref={doctorRef} className="pt-32 pb-20 bg-gradient-to-br from-stone-50 via-stone-50 to-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`transition-all duration-1000 ${doctorVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <div className="flex flex-col md:flex-row items-start gap-8 mb-8">
              {/* Doctor Image - Left Side */}
              <div className="flex-shrink-0">
                <img
                  src="https://customer-assets.emergentagent.com/job_premium-med-care/artifacts/wn1ulqot_Doctor.png"
                  alt="Dr. Allison Williams"
                  className="w-64 h-64 object-cover rounded-2xl shadow-strong border-4 border-white ring-2 ring-teal-100 hover-lift"
                />
              </div>
              {/* Heading and First Paragraph - Right Side */}
              <div className="flex-1 text-left">
                <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4">
                  Meet the Doctor
                </h1>
                <div className="ml-28 mb-6">
                  <div className="h-1 w-24 bg-gradient-to-r from-amber-500 via-amber-500 to-orange-500"></div>
                </div>
                
                {/* First Paragraph */}
                <div className="text-stone-700 leading-relaxed">
                  <p>
                    Dr. Allison N. Williams, a Diplomate of the National Board of Physicians and 
                    Surgeons, is widely recognized for her exceptional dedication to family medicine 
                    and patient care. Her skilled approach to traditional family medicine, combined 
                    with her compassionate bedside manner, has made her one of the most highly 
                    regarded clinicians in the Crofton community. In recognition of her commitment 
                    to excellence in outpatient healthcare and personalized patient care, she became 
                    an MDVIP-affiliated provider in 2025.
                  </p>
                </div>
              </div>
            </div>

            {/* Remaining Paragraphs - Below Image */}
            <div className="space-y-4 text-stone-700 leading-relaxed">
              <p>
                Crofton Family Practice benefits from Dr. Williams' extensive expertise in 
                family medicine. As Principal Owner for more than eight years and a physician 
                with over 20 years of clinical primary care experience, she is committed to 
                delivering comprehensive, patient-centered healthcare solutions. Dr. Williams 
                earned her Doctor of Medicine degree from Howard University College of Medicine 
                and completed her Family Medicine Residency at the University of Maryland, 
                Baltimore.
              </p>
              <p>
                With a strong academic foundation in Biology from Texas A&M University, Dr. 
                Williams integrates clinical knowledge, technical and molecular sciences, and 
                a deep commitment to community health into her practice. Her exceptional 
                diagnostic abilities, attention to detail, and dedication to preventive care 
                have earned her a reputation as one of the area's most trusted and respected 
                physicians.
              </p>
              <p>
                Since taking the helm from long-time owner, Ronald Sroka in 2018, Dr. Williams 
                has successfully transitioned Crofton Family Practice to meet the demands of 
                modern healthcare standards in value-based care. Under her leadership, the 
                practice has become a participant in Aledade Accountable Care Organization's 
                top performance division to further enhance the quality of services offered to 
                the patients of Crofton Family Practice since 2023. She has also been recognized 
                for clinical excellence as a preceptor in outpatient Family Medicine with Trinity 
                School of Medicine based in Saint Vincent and the Grenadines since 2019, where 
                she shapes the next generation of healthcare providers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Awards Section */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Awards & Recognitions */}
          <div className="max-w-5xl mx-auto mb-16">
            <Card className="glass-card-strong shadow-strong hover-lift card-premium backdrop-premium border-glow-multi">
              <CardContent className="p-10">
                <h3 className="text-2xl font-bold text-stone-900 mb-6 flex items-center">
                  <Award className="h-6 w-6 text-amber-600 mr-3" />
                  Awards & Recognitions
                </h3>
                <ul className="space-y-3 list-disc list-inside">
                  {credentials.map((credential) => (
                    <li key={credential.id} className="text-stone-700">
                      {credential.text}
                    </li>
                  ))}
                </ul>

                {/* Awards Images Grid */}
                <div className="mt-8">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto">
                    {/* First Row - 4 Images */}
                    <div className="aspect-square bg-white rounded-lg shadow-lg">
                      <img
                        src="https://customer-assets.emergentagent.com/job_premium-med-care/artifacts/zx8rtnln_Award%201.png"
                        alt="Trinity School of Medicine Certificate of Distinction"
                        className="w-full h-full object-contain rounded-lg hover:shadow-xl transition-shadow duration-300"
                      />
                    </div>
                    <div className="aspect-square bg-white rounded-lg shadow-lg">
                      <img
                        src="https://customer-assets.emergentagent.com/job_premium-med-care/artifacts/3wpjebev_award%202.png"
                        alt="Best in Maryland Magazine Doctors Award 2024"
                        className="w-full h-full object-contain rounded-lg hover:shadow-xl transition-shadow duration-300"
                      />
                    </div>
                    <div className="aspect-square bg-white rounded-lg shadow-lg">
                      <img
                        src="https://customer-assets.emergentagent.com/job_premium-med-care/artifacts/wb9s1c0a_Award%203.png"
                        alt="Top Family Practitioner Award 2021"
                        className="w-full h-full object-contain rounded-lg hover:shadow-xl transition-shadow duration-300"
                      />
                    </div>
                    <div className="aspect-square bg-white rounded-lg shadow-lg">
                      <img
                        src="https://customer-assets.emergentagent.com/job_premium-med-care/artifacts/gosclle8_Award%204.png"
                        alt="Trademark Women of Distinction Honoree Edition 2018"
                        className="w-full h-full object-contain rounded-lg hover:shadow-xl transition-shadow duration-300"
                      />
                    </div>
                    
                    {/* Second Row - 1 Image Centered and Smaller */}
                    <div className="col-span-2 md:col-start-2 md:col-span-2 flex justify-center">
                      <div className="aspect-square bg-white rounded-lg shadow-lg w-1/2">
                        <img
                          src="https://customer-assets.emergentagent.com/job_premium-med-care/artifacts/rbpuwkp6_Award%205.png"
                          alt="America's Top Family Doctors Award 2008"
                          className="w-full h-full object-contain rounded-lg hover:shadow-xl transition-shadow duration-300"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* MDVIP CTA */}
      <MDVIPCallToAction />
    </AnimatedPage>
  );
};

export default MeetTheDoctor;
